import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type {
  CreateOpeningBalance,
  CreatePrepaidContract,
  PrepaidContractListRow,
  UpdatePrepaidContract,
} from '@dcc-erp/shared';
import {
  CreateOpeningBalanceSchema,
  CreatePrepaidContractSchema,
  UpdatePrepaidContractSchema,
  fmt,
  fmtB,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  PREPAID_ASSET_CODE,
  PREPAID_DATE_FROM_DEFAULT,
  PREPAID_DATE_TO_DEFAULT,
  apiRowToPPItem,
  dayAfterIso,
  isExpiringSoon,
  prepaidDaysToEnd,
  useDateMode,
  type PPItem,
} from '../components/prepaid/pp-helpers';
import { PPAddModal } from '../components/prepaid/PPAddModal';
import { PPDetailDrawer } from '../components/prepaid/PPDetailDrawer';
import { PPJournal } from '../components/prepaid/PPJournal';
import { PPRegister } from '../components/prepaid/PPRegister';
import { PPRollforward } from '../components/prepaid/PPRollforward';
import { PPRunModal } from '../components/prepaid/PPRunModal';
import { PPSchedule } from '../components/prepaid/PPSchedule';
import { PPSummary } from '../components/prepaid/PPSummary';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ParsedUnit,
  type ImportResult,
  type PreviewColumn,
} from '../components/SpreadsheetImportModal';
import {
  useActiveCompany,
  useActiveCompanyId,
  useAmortizePrepaid,
  useBranches,
  useCreateOpeningBalance,
  useCreatePrepaidContract,
  useDepartments,
  useOpeningBalanceTieout,
  usePrepaidContracts,
  useUpdatePrepaidContract,
  useVendors,
} from '../lib/queries';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

type TabKey = 'register' | 'schedule' | 'journal' | 'summary' | 'rollforward';

interface Totals {
  remain: number;
  monthly: number;
  original: number;
  activeCount: number;
  pendingCount: number;
  newThisMonth: number;
}

// Map a period-selector option value to a YYYYMM string.
const PERIOD_OPTION_MAP: Record<string, string> = {
  may26: '202605',
  apr26: '202604',
  mar26: '202603',
};

// ---- Spreadsheet import/export (นำเข้า / ส่งออก) ---------------------------

// Column order / header keys for the prepaid-contract sheet. Kept identical to
// the export header so an exported file round-trips back through นำเข้า. The
// contract `code` is server-generated, so it's read-only on import (ignored).
const PP_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'description',
    label: 'รายละเอียดสัญญา',
    required: true,
    hint: 'เช่น ประกันภัยทรัพย์สินโรงงาน 12 เดือน',
  },
  {
    col: 'vendorCode',
    label: 'รหัสผู้ขาย',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริงในทะเบียนผู้ขาย',
  },
  {
    col: 'startDate',
    label: 'วันเริ่มตัดจ่าย',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-06-01 (ต้องก่อนวันสิ้นสุด)',
  },
  {
    col: 'endDate',
    label: 'วันสิ้นสุด',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2027-05-31',
  },
  {
    col: 'totalAmount',
    label: 'มูลค่าสัญญา',
    required: true,
    hint: 'ตัวเลขมากกว่า 0 เช่น 1200000',
  },
  {
    col: 'expenseAccountCode',
    label: 'รหัสบัญชีค่าใช้จ่าย',
    required: true,
    hint: 'เช่น 6230 (ประกัน), 6240 (เช่า), 6260 (IT), 6299 (อื่นๆ)',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
];

// Header row (and round-trip key order) for both the import template and the
// exporter. Read-only/computed columns are appended at the END on export only.
const PP_IMPORT_COLS = [
  'description',
  'vendorCode',
  'startDate',
  'endDate',
  'totalAmount',
  'expenseAccountCode',
  'branchCode',
  'departmentCode',
] as const;

const PP_TEMPLATE_EXAMPLES: (string | number | boolean)[][] = [
  [
    'ประกันภัยทรัพย์สินโรงงาน 1+2 (12 เดือน)',
    '',
    '2026-06-01',
    '2027-05-31',
    1_200_000,
    '6230',
    '',
    '',
  ],
  ['ค่าเช่าโกดังสมุทรปราการ (12 เดือน)', '', '2026-06-01', '2027-05-31', 3_360_000, '6240', '', ''],
];

// ---- Opening-balance take-on import (go-live migration) ----------------------

const PP_OB_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'description',
    label: 'รายละเอียดสัญญา',
    required: true,
    hint: 'เช่น ประกันภัยทรัพย์สินโรงงาน 12 เดือน',
  },
  {
    col: 'vendorCode',
    label: 'รหัสผู้ขาย',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริงในทะเบียนผู้ขาย',
  },
  {
    col: 'startDate',
    label: 'วันเริ่มตัดจ่าย',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2025-07-01',
  },
  {
    col: 'endDate',
    label: 'วันสิ้นสุด',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-06-30',
  },
  {
    col: 'totalAmount',
    label: 'มูลค่าสัญญา',
    required: true,
    hint: 'ตัวเลขมากกว่า 0 เช่น 1200000',
  },
  {
    col: 'expenseAccountCode',
    label: 'รหัสบัญชีค่าใช้จ่าย',
    required: true,
    hint: 'เช่น 6230 (ประกัน), 6240 (เช่า), 6260 (IT), 6299 (อื่นๆ)',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'asOfPeriod',
    label: 'งวด ณ go-live (YYYYMM)',
    required: true,
    hint: 'เช่น 202606 (มิถุนายน 2026)',
  },
  {
    col: 'monthsAmortizedSoFar',
    label: 'ตัดจ่ายไปแล้ว (งวด)',
    required: true,
    hint: 'จำนวนงวดที่ตัดจ่ายไปแล้วก่อน go-live เช่น 5',
  },
];

const PP_OB_IMPORT_COLS = [
  'description',
  'vendorCode',
  'startDate',
  'endDate',
  'totalAmount',
  'expenseAccountCode',
  'branchCode',
  'departmentCode',
  'asOfPeriod',
  'monthsAmortizedSoFar',
] as const;

const PP_OB_TEMPLATE_EXAMPLES: (string | number | boolean)[][] = [
  [
    'ประกันภัยทรัพย์สินโรงงาน 1+2 (12 เดือน)',
    '',
    '2026-01-01',
    '2026-12-31',
    1_200_000,
    '6230',
    '',
    '',
    '202606',
    5,
  ],
];

// ---- Bulk-update import (แก้ไขหลายสัญญาพร้อมกัน) ----------------------------

const PP_UPD_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'code',
    label: 'รหัสสัญญา',
    required: true,
    hint: 'เช่น PPE-2026-000003 — ใช้จับคู่กับสัญญาที่มีอยู่',
  },
  {
    col: 'description',
    label: 'รายละเอียดสัญญา (ใหม่)',
    required: false,
    hint: 'เว้นว่างเพื่อคงค่าเดิม',
  },
  {
    col: 'vendorCode',
    label: 'รหัสผู้ขาย (ใหม่)',
    required: false,
    hint: 'เว้นว่างเพื่อคงค่าเดิม',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา (ใหม่)',
    required: false,
    hint: 'เว้นว่างเพื่อคงค่าเดิม',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน (ใหม่)',
    required: false,
    hint: 'เว้นว่างเพื่อคงค่าเดิม',
  },
];

const PP_UPD_IMPORT_COLS = [
  'code',
  'description',
  'vendorCode',
  'branchCode',
  'departmentCode',
] as const;

const PP_UPD_TEMPLATE_EXAMPLES: (string | number | boolean)[][] = [
  ['PPE-2026-000003', 'แคมเปญโฆษณา TV ออกอากาศ Q2-Q3 2026 (6 เดือน) — แก้ไขรายละเอียด', '', '', ''],
];

export const Route = createFileRoute('/prepaid')({
  component: PrepaidPage,
});

export function PrepaidPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const { df } = useDateMode();
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const { data: apiRows } = usePrepaidContracts(companyId, {
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
  });
  const createMut = useCreatePrepaidContract(companyId);
  const amortizeMut = useAmortizePrepaid(companyId);
  const openingBalanceMut = useCreateOpeningBalance(companyId);
  const updateContractMut = useUpdatePrepaidContract(companyId);
  const { data: tieout } = useOpeningBalanceTieout(companyId);

  // Masters for code↔id resolution on import/export.
  const { data: vendors = [] } = useVendors(companyId);
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [showOpeningImport, setShowOpeningImport] = useState<boolean>(false);
  const [showUpdateImport, setShowUpdateImport] = useState<boolean>(false);

  const vendorByCode = useMemo(
    () => new Map(vendors.map((v) => [v.code.toLowerCase(), v.id])),
    [vendors],
  );
  const vendorCodeById = useMemo(() => new Map(vendors.map((v) => [v.id, v.code])), [vendors]);
  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const departmentByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  const EXPORT_COLS: ExportColumn<PrepaidContractListRow>[] = [
    { header: 'code', value: (r) => r.code, width: 18 },
    {
      header: 'vendorCode',
      value: (r) => (r.vendorId ? (vendorCodeById.get(r.vendorId) ?? '') : ''),
      width: 14,
    },
    { header: 'description', value: (r) => r.description, width: 40 },
    { header: 'expenseAccountCode', value: (r) => r.expenseAccountCode, width: 16 },
    { header: 'startDate', value: (r) => String(r.startDate).slice(0, 10), width: 12 },
    { header: 'endDate', value: (r) => String(r.endDate).slice(0, 10), width: 12 },
    { header: 'totalAmount', value: (r) => Number(r.totalAmount), width: 14 },
    { header: 'monthlyAmount', value: (r) => Number(r.monthlyAmount), width: 14 },
    { header: 'monthsTotal', value: (r) => r.monthsTotal, width: 10 },
    { header: 'monthsAmortized', value: (r) => r.monthsAmortized, width: 12 },
    { header: 'remainingAmount', value: (r) => Number(r.remainingAmount), width: 14 },
    { header: 'status', value: (r) => r.status, width: 14 },
  ];

  async function handleExport(): Promise<void> {
    const filename = `ค่าใช้จ่ายจ่ายล่วงหน้า_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`;
    await exportRows(filename, 'ค่าใช้จ่ายจ่ายล่วงหน้า', EXPORT_COLS, apiRows ?? []);
  }

  function parsePrepaid(grid: string[][]): {
    units: ParsedUnit<CreatePrepaidContract>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of [
      'description',
      'startDate',
      'endDate',
      'totalAmount',
      'expenseAccountCode',
    ]) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreatePrepaidContract>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const vendorCode = cell(cols, 'vendorCode');
      const vendorId = vendorCode ? vendorByCode.get(vendorCode.toLowerCase()) : null;
      if (vendorCode && !vendorId) {
        units.push({ rowNo, data: null, error: `ไม่พบผู้ขาย: ${vendorCode}` });
        continue;
      }
      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : null;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? departmentByCode.get(departmentCode.toLowerCase())
        : null;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const candidate = {
        description: cell(cols, 'description'),
        vendorId: vendorId ?? null,
        startDate: cell(cols, 'startDate'),
        endDate: cell(cols, 'endDate'),
        totalAmount: Number(cell(cols, 'totalAmount')),
        expenseAccountCode: cell(cols, 'expenseAccountCode'),
        branchId: branchId ?? null,
        departmentId: departmentId ?? null,
        // cashAccountCode omitted → service resolves the per-company BANK_PRIMARY.
      };
      const p = CreatePrepaidContractSchema.safeParse(candidate);
      units.push(
        p.success
          ? { rowNo, data: p.data, error: null }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreatePrepaidContract>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createMut.mutateAsync({ data: u.data, idempotencyKey: crypto.randomUUID() });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  // --- Opening-balance (go-live take-on) parse + run -------------------------

  function parseOpeningBalance(grid: string[][]): {
    units: ParsedUnit<CreateOpeningBalance>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of [
      'description',
      'startDate',
      'endDate',
      'totalAmount',
      'expenseAccountCode',
      'asOfPeriod',
      'monthsAmortizedSoFar',
    ]) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreateOpeningBalance>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const vendorCode = cell(cols, 'vendorCode');
      const vendorId = vendorCode ? vendorByCode.get(vendorCode.toLowerCase()) : null;
      if (vendorCode && !vendorId) {
        units.push({ rowNo, data: null, error: `ไม่พบผู้ขาย: ${vendorCode}` });
        continue;
      }
      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : null;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? departmentByCode.get(departmentCode.toLowerCase())
        : null;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const candidate = {
        description: cell(cols, 'description'),
        vendorId: vendorId ?? null,
        startDate: cell(cols, 'startDate'),
        endDate: cell(cols, 'endDate'),
        totalAmount: Number(cell(cols, 'totalAmount')),
        expenseAccountCode: cell(cols, 'expenseAccountCode'),
        branchId: branchId ?? null,
        departmentId: departmentId ?? null,
        asOfPeriod: cell(cols, 'asOfPeriod'),
        monthsAmortizedSoFar: Number(cell(cols, 'monthsAmortizedSoFar')),
      };
      const p = CreateOpeningBalanceSchema.safeParse(candidate);
      units.push(
        p.success
          ? { rowNo, data: p.data, error: null }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runOpeningImport(
    valid: ParsedUnit<CreateOpeningBalance>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await openingBalanceMut.mutateAsync({
          data: u.data,
          idempotencyKey: crypto.randomUUID(),
        });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  // --- Bulk-update parse + run -----------------------------------------------

  function parseUpdate(grid: string[][]): {
    units: ParsedUnit<{ code: string; id: string; data: UpdatePrepaidContract }>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    if (idx('code') === -1) return { units: [], headerError: 'ไม่พบคอลัมน์ที่จำเป็น: code' };
    const units: ParsedUnit<{ code: string; id: string; data: UpdatePrepaidContract }>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const code = cell(cols, 'code');
      const contractRow = apiRows?.find((row) => row.code === code);
      if (!contractRow) {
        units.push({ rowNo, data: null, error: `ไม่พบสัญญา: ${code}` });
        continue;
      }

      const vendorCode = cell(cols, 'vendorCode');
      const vendorId = vendorCode ? vendorByCode.get(vendorCode.toLowerCase()) : undefined;
      if (vendorCode && !vendorId) {
        units.push({ rowNo, data: null, error: `ไม่พบผู้ขาย: ${vendorCode}` });
        continue;
      }
      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : undefined;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? departmentByCode.get(departmentCode.toLowerCase())
        : undefined;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const descRaw = cell(cols, 'description');
      const candidate: Record<string, unknown> = {};
      if (descRaw) candidate.description = descRaw;
      if (vendorId !== undefined) candidate.vendorId = vendorId;
      if (branchId !== undefined) candidate.branchId = branchId;
      if (departmentId !== undefined) candidate.departmentId = departmentId;

      const p = UpdatePrepaidContractSchema.safeParse(candidate);
      units.push(
        p.success
          ? {
              rowNo,
              data: { code, id: contractRow.id, data: p.data },
              error: null,
              action: 'update' as const,
            }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runUpdateImport(
    valid: ParsedUnit<{ code: string; id: string; data: UpdatePrepaidContract }>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await updateContractMut.mutateAsync({ id: u.data.id, data: u.data.data });
        res.updated++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo} (${u.data.code})`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  // Real backend rows only — no mock fallback. An empty contract list renders a
  // real empty-state instead of fabricated sample contracts.
  const items = useMemo<readonly PPItem[]>(
    () => (apiRows ?? []).map(apiRowToPPItem),
    [apiRows],
  );

  const [tab, setTab] = useState<TabKey>('register');
  const [q, setQ] = useState<string>('');
  const [catF, setCatF] = useState<string>('all');
  const [statusF, setStatusF] = useState<string>('all');
  // P9 §3.7 — "ใกล้ครบกำหนด" filter axis (drives the saved-view preset + its chip).
  const [expSoon, setExpSoon] = useState<boolean>(false);
  const [dateFrom, setDateFrom] = useState<string>(PREPAID_DATE_FROM_DEFAULT);
  const [dateTo, setDateTo] = useState<string>(PREPAID_DATE_TO_DEFAULT);
  const [periodSel, setPeriodSel] = useState<string>('may26');
  const [showAdd, setShowAdd] = useState<boolean>(false);
  // P8 — renew/clone source (prefills the add modal from an expiring contract).
  const [cloneSource, setCloneSource] = useState<PPItem | null>(null);
  const [showRun, setShowRun] = useState<boolean>(false);
  // Schedule-tab selection is derived (not stored) so it always stays valid as
  // contracts load/change: the chosen id, else the first contract, else none.
  const [selScheduleId, setSelScheduleId] = useState<string | null>(null);
  const selSchedule = useMemo<PPItem | null>(
    () => items.find((i) => i.id === selScheduleId) ?? items[0] ?? null,
    [items, selScheduleId],
  );
  // P7: a register row opens the detail drawer (the host surface) instead of the
  // jarring full-tab switch; the Schedule tab is kept (via the tab bar) for print.
  const [drawerItem, setDrawerItem] = useState<PPItem | null>(null);
  // Re-derive the open drawer's row from the live list so derived fields (e.g.
  // missingEvidenceCount after an in-drawer upload) stay fresh; fall back to the
  // captured snapshot for mock rows (no apiId) that aren't in `items`.
  const liveDrawerItem = useMemo<PPItem | null>(() => {
    if (!drawerItem) return null;
    if (!drawerItem.apiId) return drawerItem;
    return items.find((i) => i.apiId === drawerItem.apiId) ?? drawerItem;
  }, [drawerItem, items]);

  const filtered = useMemo<readonly PPItem[]>(
    () =>
      items.filter((r) => {
        if (catF !== 'all' && r.cat !== catF) return false;
        if (statusF !== 'all' && r.status !== statusF) return false;
        if (expSoon && !isExpiringSoon(r)) return false;
        if (dateFrom && r.start < dateFrom) return false;
        if (dateTo && r.start > dateTo) return false;
        if (
          q &&
          !(
            r.id.toLowerCase().includes(q.toLowerCase()) ||
            r.vendor.toLowerCase().includes(q.toLowerCase()) ||
            r.desc.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [items, q, catF, statusF, expSoon, dateFrom, dateTo],
  );

  const totals = useMemo<Totals>(() => {
    const active = items.filter((r) => r.status === 'active');
    return {
      remain: active.reduce((s, r) => s + r.monthly * r.remain, 0),
      monthly: active.reduce((s, r) => s + r.monthly, 0),
      original: items.reduce((s, r) => s + r.total, 0),
      activeCount: active.length,
      pendingCount: items.filter((r) => r.status === 'pending').length,
      newThisMonth: items.filter(
        (r) => r.start.startsWith('2026-05') || r.start.startsWith('2026-06'),
      ).length,
    };
  }, [items]);

  const thisMonthItems = useMemo<readonly PPItem[]>(
    () => items.filter((r) => r.status === 'active'),
    [items],
  );

  // P8 — expiry/renewal alerts. Live (non-terminated/suspended/completed) contracts
  // whose schedule ends within 90 days, soonest first. Drives the "ใกล้ครบ" KPI +
  // the alerts strip + one-click renew/clone.
  const expiring = useMemo<readonly { item: PPItem; days: number }[]>(() => {
    const out: { item: PPItem; days: number }[] = [];
    for (const r of items) {
      if (!isExpiringSoon(r)) continue;
      const days = prepaidDaysToEnd(r.end);
      if (days === null) continue;
      out.push({ item: r, days });
    }
    return out.sort((a, b) => a.days - b.days);
  }, [items]);

  // Current period in YYYYMM — driven by the period selector in the header.
  // Falls back to today's YYYYMM if the option isn't in the map.
  const currentPeriod = useMemo<string>(() => {
    const mapped = PERIOD_OPTION_MAP[periodSel];
    if (mapped) return mapped;
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, [periodSel]);

  const onCreateContract = (data: CreatePrepaidContract): void => {
    if (!companyId) return;
    createMut.mutate({ data });
    setShowAdd(false);
  };

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['register', 'ทะเบียนสัญญา'],
    ['schedule', 'ตารางตัดจ่ายรายเดือน'],
    ['journal', 'ประวัติการตัดจ่าย (JV)'],
    ['summary', 'สรุปตามหมวด'],
    ['rollforward', 'กระทบยอด & Roll-forward'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'ค่าใช้จ่ายจ่ายล่วงหน้า']}
        title="ค่าใช้จ่ายจ่ายล่วงหน้า (Prepaid Expenses)"
        actions={
          <>
            <HelpButton guide="prepaid" />
            <select
              className="select"
              value={periodSel}
              onChange={(e) => setPeriodSel(e.target.value)}
            >
              <option value="may26">งวด: พ.ค. 2026</option>
              <option value="apr26">งวด: เม.ย. 2026</option>
              <option value="mar26">งวด: มี.ค. 2026</option>
            </select>
            <button className="btn" onClick={() => setShowRun(true)}>
              <Icon name="refresh" size={14} /> รันตัดจ่ายเดือน
            </button>
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => setShowOpeningImport(true)}
              disabled={!companyId}
            >
              <Icon name="upload" size={14} /> นำเข้ายอดยกมา
            </button>
            <button className="btn" onClick={() => setShowUpdateImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้าแก้ไข
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={(apiRows?.length ?? 0) === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowAdd(true)}>
              <Icon name="plus" size={14} /> ลงทะเบียนใหม่
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ยอดคงเหลือรวม ({PREPAID_ASSET_CODE})
          </div>
          <div className="value tnum">
            {fmtB(totals.remain)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{totals.activeCount} สัญญายังตัดจ่ายไม่หมด</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ตัดจ่ายเดือนนี้ (พ.ค.)
          </div>
          <div className="value tnum">
            {fmtB(totals.monthly)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{thisMonthItems.length} รายการรอประมวลผล</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            มูลค่าสัญญารวม
          </div>
          <div className="value tnum">
            {fmtB(totals.original)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{items.length} สัญญา · YTD</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            สัญญาใหม่เริ่ม
          </div>
          <div className="value tnum">
            {totals.newThisMonth} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">{totals.pendingCount} ฉบับยังไม่เริ่มตัดจ่าย</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span
              className="dot"
              style={{ background: expiring.length > 0 ? 'var(--negative)' : 'var(--positive)' }}
            />
            ใกล้ครบ (90 วัน)
          </div>
          <div className="value tnum">
            {expiring.length} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">
            {expiring.filter((e) => e.days <= 30).length} ฉบับครบใน 30 วัน
          </div>
        </div>
      </div>

      {/* P8 — expiry/renewal alerts strip (one-click renew/clone). */}
      {expiring.length > 0 && (
        <div
          style={{
            border: '1px solid var(--warning, #b4780a)',
            borderLeft: '4px solid var(--warning, #b4780a)',
            background: 'rgba(180,120,10,0.06)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 'var(--section-gap)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--warning,#b4780a)' }}>
            ⚠ สัญญาใกล้ครบกำหนด ({expiring.length} รายการ)
          </div>
          <div style={{ display: 'grid', gap: 6, maxHeight: 168, overflow: 'auto' }}>
            {expiring.slice(0, 8).map(({ item, days }) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 13,
                  padding: '4px 0',
                }}
              >
                <span className="code" style={{ minWidth: 130 }}>
                  {item.id}
                </span>
                <span
                  style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {item.vendor} · {item.desc}
                </span>
                <span
                  className="badge"
                  style={{
                    color: days <= 30 ? 'var(--negative)' : 'var(--warning,#b4780a)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {days <= 0 ? 'ครบแล้ว' : `อีก ${days} วัน`}
                </span>
                <span className="meta" style={{ whiteSpace: 'nowrap' }}>
                  สิ้นสุด {df(item.end)}
                </span>
                <button
                  className="btn sm"
                  onClick={() => {
                    setCloneSource(item);
                    setShowAdd(true);
                  }}
                >
                  ต่ออายุ / คัดลอก
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          background: 'linear-gradient(90deg, var(--brand-tint), white 70%)',
          border: '1px solid var(--brand-tint-2)',
          borderLeft: '4px solid var(--brand)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--brand)',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="refresh" size={22} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)' }}>
            ตัดจ่ายค่าใช้จ่ายล่วงหน้าเดือน พ.ค. 2026 · ยังไม่ได้ประมวลผล
          </div>
          <div className="meta" style={{ marginTop: 2 }}>
            จะสร้างใบสำคัญ {thisMonthItems.length} ฉบับ · ยอดตัดจ่ายรวม{' '}
            <b style={{ color: 'var(--brand)' }}>{fmt(totals.monthly)}</b> บาท ·
            กำหนดประมวลผลทุกวันสิ้นเดือน (31 พ.ค. 2026)
          </div>
        </div>
        <button className="btn primary" onClick={() => setShowRun(true)}>
          <Icon name="check" size={14} /> ตรวจสอบ & ประมวลผล
        </button>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <PPRegister
          rows={filtered}
          items={items}
          q={q}
          setQ={setQ}
          catF={catF}
          setCatF={setCatF}
          statusF={statusF}
          setStatusF={setStatusF}
          expSoon={expSoon}
          setExpSoon={setExpSoon}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          companyId={companyId}
          filterBranchId={filterBranchId}
          setFilterBranchId={setFilterBranchId}
          filterDepartmentId={filterDepartmentId}
          setFilterDepartmentId={setFilterDepartmentId}
          onPick={(r) => {
            setSelScheduleId(r.id);
            setDrawerItem(r);
          }}
        />
      )}
      {tab === 'schedule' &&
        (selSchedule ? (
          <PPSchedule
            item={selSchedule}
            setItem={(i) => setSelScheduleId(i.id)}
            companyId={companyId}
            items={items}
          />
        ) : (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            ยังไม่มีสัญญาค่าใช้จ่ายล่วงหน้า — กด “เพิ่มสัญญา” ในแท็บทะเบียนเพื่อเริ่มต้น
          </div>
        ))}
      {tab === 'journal' && <PPJournal companyId={companyId} />}
      {tab === 'summary' && (
        <>
          {tieout && tieout.migratedContracts > 0 && (
            <div
              className="card"
              style={{ marginBottom: 'var(--section-gap)', padding: '16px 18px' }}
            >
              <div className="card-head" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>กระทบยอดยอดยกมา (Go-live tie-out)</h3>
                <span className="meta">
                  ยอดยกมา {tieout.migratedContracts} สัญญา — ตรวจสอบความถูกต้องของการ migrate
                </span>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 18,
                }}
              >
                <div
                  className="kpi"
                  style={{
                    boxShadow: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <div className="label">สัญญาที่ยกมา</div>
                  <div className="value tnum" style={{ fontSize: 22 }}>
                    {tieout.migratedContracts} <span className="unit">สัญญา</span>
                  </div>
                </div>
                <div
                  className="kpi"
                  style={{
                    boxShadow: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <div className="label">ยอด JV เปิด (Dr {PREPAID_ASSET_CODE})</div>
                  <div className="value tnum" style={{ fontSize: 22 }}>
                    {fmtB(Number(tieout.openingBalanceTotal))} <span className="unit">บาท</span>
                  </div>
                </div>
                <div
                  className="kpi"
                  style={{
                    boxShadow: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <div className="label">คงเหลือตามทะเบียน</div>
                  <div className="value tnum" style={{ fontSize: 22 }}>
                    {fmtB(Number(tieout.migratedRemaining))} <span className="unit">บาท</span>
                  </div>
                </div>
                <div
                  className="kpi"
                  style={{
                    boxShadow: 'none',
                    border: `1px solid ${Number(tieout.variance) === 0 ? 'var(--positive)' : 'var(--negative)'}`,
                    borderRadius: 8,
                    padding: '12px 14px',
                    background:
                      Number(tieout.variance) === 0
                        ? 'var(--positive-bg, #f0fdf4)'
                        : 'var(--negative-bg, #fef2f2)',
                  }}
                >
                  <div className="label">ส่วนต่าง (Variance)</div>
                  <div
                    className="value tnum"
                    style={{
                      fontSize: 22,
                      color: Number(tieout.variance) === 0 ? 'var(--positive)' : 'var(--negative)',
                    }}
                  >
                    {fmtB(Number(tieout.variance))} <span className="unit">บาท</span>
                  </div>
                  <div className="trend" style={{ marginTop: 4 }}>
                    {Number(tieout.variance) === 0 ? (
                      <span className="badge positive">
                        <span className="bdot" /> กระทบยอดแล้ว
                      </span>
                    ) : (
                      <span
                        className="badge"
                        style={{
                          color: 'var(--negative)',
                          background: 'var(--negative-bg, #fef2f2)',
                          border: '1px solid var(--negative)',
                        }}
                      >
                        <span className="bdot" style={{ background: 'var(--negative)' }} />{' '}
                        มีส่วนต่าง — ตรวจสอบ
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <PPSummary items={items} />
        </>
      )}
      {tab === 'rollforward' && <PPRollforward companyId={companyId} />}

      {liveDrawerItem && (
        <PPDetailDrawer
          item={liveDrawerItem}
          companyId={companyId}
          onClose={() => setDrawerItem(null)}
          onClone={(r) => {
            setCloneSource(r);
            setShowAdd(true);
            setDrawerItem(null);
          }}
        />
      )}

      {showAdd && (
        <PPAddModal
          companyId={companyId}
          onClose={() => {
            setShowAdd(false);
            setCloneSource(null);
          }}
          onSubmit={onCreateContract}
          submitting={createMut.isPending}
          existing={items}
          {...(cloneSource
            ? {
                prefill: {
                  cat: cloneSource.cat,
                  total: cloneSource.total,
                  period: cloneSource.period,
                  description: `${cloneSource.desc} (ต่ออายุ)`,
                  start: dayAfterIso(cloneSource.end),
                },
              }
            : {})}
        />
      )}
      {showRun && (
        <PPRunModal
          companyId={companyId}
          initialPeriod={currentPeriod}
          onClose={() => setShowRun(false)}
          submitting={amortizeMut.isPending}
          amortizeMut={amortizeMut}
        />
      )}
      {showImport && companyId && (
        <SpreadsheetImportModal<CreatePrepaidContract>
          title="นำเข้าค่าใช้จ่ายจ่ายล่วงหน้า"
          fields={PP_IMPORT_FIELDS}
          templateColumns={[...PP_IMPORT_COLS]}
          templateExamples={PP_TEMPLATE_EXAMPLES}
          templateFilename="prepaid_template.xlsx"
          sheetName="ค่าใช้จ่ายจ่ายล่วงหน้า"
          parse={parsePrepaid}
          previewColumns={PP_PREVIEW_COLS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
      {showOpeningImport && companyId && (
        <SpreadsheetImportModal<CreateOpeningBalance>
          title="นำเข้ายอดยกมา (Go-live)"
          intro={
            <>
              นำเข้าสัญญาที่เริ่มก่อน go-live — ระบุงวดและจำนวนงวดที่ตัดจ่ายไปแล้วเพื่อให้ระบบ สร้าง
              JV เปิดบัญชีและคำนวณตารางตัดจ่ายต่อที่ถูกต้อง
            </>
          }
          fields={PP_OB_IMPORT_FIELDS}
          templateColumns={[...PP_OB_IMPORT_COLS]}
          templateExamples={PP_OB_TEMPLATE_EXAMPLES}
          templateFilename="prepaid_opening_balance_template.xlsx"
          sheetName="ยอดยกมา"
          parse={parseOpeningBalance}
          previewColumns={PP_OB_PREVIEW_COLS}
          runImport={runOpeningImport}
          onClose={() => setShowOpeningImport(false)}
        />
      )}
      {showUpdateImport && companyId && (
        <SpreadsheetImportModal<{ code: string; id: string; data: UpdatePrepaidContract }>
          title="นำเข้าแก้ไขสัญญา"
          intro={
            <>
              แก้ไขข้อมูลส่วนหัวของสัญญาหลายรายการพร้อมกัน — ระบุ <code>code</code>{' '}
              เพื่อจับคู่กับสัญญาที่มีอยู่ คอลัมน์ที่เว้นว่างจะคงค่าเดิม
            </>
          }
          fields={PP_UPD_IMPORT_FIELDS}
          templateColumns={[...PP_UPD_IMPORT_COLS]}
          templateExamples={PP_UPD_TEMPLATE_EXAMPLES}
          templateFilename="prepaid_update_template.xlsx"
          sheetName="แก้ไขสัญญา"
          parse={parseUpdate}
          previewColumns={PP_UPD_PREVIEW_COLS}
          showUpdate={true}
          runImport={runUpdateImport}
          onClose={() => setShowUpdateImport(false)}
        />
      )}
    </div>
  );
}

// Preview columns shown in the import dialog before committing each row.
const PP_PREVIEW_COLS: PreviewColumn<CreatePrepaidContract>[] = [
  { header: 'รายละเอียด', render: (d) => d.description },
  { header: 'บัญชีค่าใช้จ่าย', width: 120, render: (d) => d.expenseAccountCode },
  { header: 'เริ่ม', width: 100, render: (d) => String(d.startDate).slice(0, 10) },
  { header: 'สิ้นสุด', width: 100, render: (d) => String(d.endDate).slice(0, 10) },
  { header: 'มูลค่าสัญญา', width: 120, render: (d) => fmt(d.totalAmount) },
];

const PP_OB_PREVIEW_COLS: PreviewColumn<CreateOpeningBalance>[] = [
  { header: 'รายละเอียด', render: (d) => d.description },
  { header: 'บัญชีค่าใช้จ่าย', width: 120, render: (d) => d.expenseAccountCode },
  { header: 'เริ่ม', width: 100, render: (d) => String(d.startDate).slice(0, 10) },
  { header: 'สิ้นสุด', width: 100, render: (d) => String(d.endDate).slice(0, 10) },
  { header: 'มูลค่าสัญญา', width: 120, render: (d) => fmt(d.totalAmount) },
  { header: 'งวด go-live', width: 100, render: (d) => d.asOfPeriod },
  { header: 'ตัดไปแล้ว', width: 80, render: (d) => `${d.monthsAmortizedSoFar} งวด` },
];

const PP_UPD_PREVIEW_COLS: PreviewColumn<{
  code: string;
  id: string;
  data: UpdatePrepaidContract;
}>[] = [
  { header: 'รหัสสัญญา', width: 160, render: (d) => d.code },
  { header: 'รายละเอียดใหม่', render: (d) => d.data.description ?? '(คงเดิม)' },
];
